import { useEffect, useRef, type ReactNode } from 'react';
import { useAnimationFrame, useReducedMotion } from 'motion/react';
import { useLingui } from '@lingui/react/macro';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Heart, Zap } from 'lucide-react';
import { cn, formatNumber } from '@worldcoo/ui/lib/utils';
import { trpc } from '@/lib/trpc';
import { useTranslation } from '@/lib/i18n';
import { Image } from '@/routes/-components/image';
import { LottiePlayer } from '../lottie/lottie-player';
import heroHandsAnimation from '../lottie/hero-home.json';

/**
 * `valueClassName` exists for the two figures shown in full (total raised, microdonations):
 * nine digits plus separators do not fit the card at the display size the short counters use.
 */
function StatValue(props: { value: string; label: string; valueClassName?: string }) {
	return (
		<>
			<p className={cn('text-4xl font-semibold tabular-nums md:text-5xl', props.valueClassName)}>{props.value}</p>
			<p className="mt-1 text-sm font-medium opacity-80 md:text-base">{props.label}</p>
		</>
	);
}

/*
 * The bento's tiles, each taking a `className` for its footprint. They are components rather than
 * inline JSX because the mobile and desktop layouts are different arrangements of the SAME tiles —
 * mobile swipes them in two rows, desktop packs them into a three-column grid — and duplicating the
 * markup would let the two drift apart.
 */

function FundedProjectsTile(props: { value: string; className?: string }) {
	const { t } = useLingui();
	return (
		<div className={cn('bg-peach text-ink flex flex-col rounded-3xl p-6 md:p-7', props.className)}>
			<Zap className="text-brand-strong size-6 fill-current" />
			<div className="mt-auto pt-6">
				<StatValue value={props.value} label={t`Funded projects`} />
			</div>
		</div>
	);
}

function CoinsTile(props: { className?: string }) {
	const { t } = useLingui();
	return (
		<div className={cn('bg-sand flex items-center justify-center overflow-hidden rounded-3xl p-2', props.className)}>
			{/* Fills the card edge to edge (only enough padding to keep it off the rounded corners) — the
			    comms team asked for the illustration bigger. */}
			<LottiePlayer
				animationData={heroHandsAnimation}
				ariaLabel={t`Hands catching euro coins`}
				className="h-full max-h-64 w-full"
			/>
		</div>
	);
}

function TotalRaisedTile(props: { value: string; className?: string }) {
	const { t } = useLingui();
	return (
		<div className={cn('relative overflow-hidden rounded-3xl', props.className)}>
			<Image
				src="/redesign/hero-hands.jpg"
				alt={t`Two hands holding each other`}
				height={480}
				aspectRatio={3 / 4}
				objectFit="cover"
				className="absolute inset-0 h-full w-full object-cover!"
			/>
			<div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-6 text-white md:p-7">
				<StatValue value={props.value} label={t`Total raised`} valueClassName="text-3xl md:text-4xl" />
			</div>
		</div>
	);
}

function MicrodonationsTile(props: { value: string; className?: string }) {
	const { t } = useLingui();
	return (
		<div
			className={cn(
				'bg-brand text-ink relative flex flex-col justify-end overflow-hidden rounded-3xl p-6 md:p-7',
				props.className,
			)}
		>
			<div aria-hidden className="absolute -top-10 -right-10 size-40 rounded-full border-[16px] border-white/15" />
			<StatValue value={props.value} label={t`Microdonations received`} valueClassName="text-3xl md:text-4xl" />
		</div>
	);
}

function PartnerNgosTile(props: { value: string; className?: string }) {
	const { t } = useLingui();
	return (
		<div className={cn('bg-ink flex flex-col rounded-3xl p-6 text-white md:p-7', props.className)}>
			<Heart className="text-olive size-6 fill-current" />
			<div className="mt-auto pt-6">
				<p className="text-olive text-4xl font-semibold tabular-nums md:text-5xl">{props.value}</p>
				<p className="mt-1 text-sm font-medium text-white/80 md:text-base">{t`Partner NGOs`}</p>
			</div>
		</div>
	);
}

/**
 * A photo tile. `height`/`aspectRatio` are identical to the wide-screen side photos on purpose: the
 * imgproxy URL is derived from them, so reusing the values means mobile and desktop request the very
 * same file instead of two crops of it.
 */
function PhotoTile(props: { src: string; alt: string; className?: string }) {
	return (
		<div className={cn('overflow-hidden rounded-3xl', props.className)}>
			<Image
				src={props.src}
				alt={props.alt}
				height={288}
				aspectRatio={220 / 288}
				objectFit="cover"
				className="h-full w-full object-cover!"
			/>
		</div>
	);
}

// px per ms — ~15px/s, slow enough to be a hint that the row continues rather than something moving
// under you while you read it.
const DRIFT_PX_PER_MS = 0.015;
const DRIFT_RESUME_MS = 2500;

/**
 * One horizontally swipeable row that also drifts on its own, so it reads as continuing past the screen
 * edge rather than looking cut off.
 *
 * The drift animates `scrollLeft`, not a `transform`. A transform marquee — what the partner rows use —
 * cannot coexist with native scrolling, and native scrolling is what gives touch dragging, momentum and
 * the keyboard for free. So the row stays a plain scroller and the animation only nudges it.
 *
 * It gets out of the way on contact: a pointer press pauses it and it resumes a couple of seconds after
 * release, so it never fights a finger or pulls content from under someone reading. It ping-pongs at the
 * ends rather than looping, which avoids duplicating every tile into the DOM, and it runs only while the
 * row is on screen — animating `scrollLeft` costs layout each frame, unlike a compositor-driven
 * transform, so it should not burn a phone's battery off-screen.
 *
 * The position is accumulated in a ref, NOT read back from `scrollLeft` each frame. At this speed a
 * frame advances about a quarter of a pixel, and reading the property back rounds that away, so
 * accumulating through the DOM left the row permanently at zero. The ref is re-synced from the element
 * whenever a finger has moved it, so the drift carries on from wherever it was left.
 */
function SwipeRow(props: { children: ReactNode; speed: number }) {
	const ref = useRef<HTMLDivElement>(null);
	const reduce = useReducedMotion() ?? false;
	const direction = useRef(1);
	const offset = useRef(0);
	const held = useRef(false);
	const resumeAt = useRef(0);
	const onScreen = useRef(false);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		const observer = new IntersectionObserver(([entry]) => {
			onScreen.current = entry?.isIntersecting ?? false;
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	useAnimationFrame((time, delta) => {
		const el = ref.current;
		if (!el || reduce || !onScreen.current || held.current || time < resumeAt.current) return;
		const max = el.scrollWidth - el.clientWidth;
		if (max <= 0) return;

		// Snapping belongs to finger-driven swipes only. Left on during the drift, even at `proximity`,
		// the browser treats each quarter-pixel step as a scroll that has settled and pulls the row to
		// the nearest tile — the drift became a 288px jump per tile instead of a slow slide.
		if (el.style.scrollSnapType !== 'none') el.style.scrollSnapType = 'none';

		offset.current += direction.current * delta * props.speed;
		if (offset.current >= max) {
			offset.current = max;
			direction.current = -1;
		}
		if (offset.current <= 0) {
			offset.current = 0;
			direction.current = 1;
		}
		el.scrollLeft = offset.current;
	});

	const release = () => {
		held.current = false;
		// Pick up from where the finger left it, rather than snapping back to our own stale offset.
		offset.current = ref.current?.scrollLeft ?? offset.current;
		resumeAt.current = performance.now() + DRIFT_RESUME_MS;
	};

	return (
		<div
			ref={ref}
			// `snap-x` is proximity, not mandatory: mandatory snapping fights a scroll that is animated
			// frame by frame, tugging it back to the nearest tile.
			className="hide-scrollbar flex snap-x [scroll-padding-left:1.5rem] gap-4 overflow-x-auto px-6"
			onPointerDown={(e) => {
				held.current = true;
				// Hand snapping back to CSS for the duration of the swipe, so releasing settles on a tile.
				e.currentTarget.style.scrollSnapType = '';
			}}
			onPointerUp={release}
			onPointerCancel={release}
		>
			{props.children}
		</div>
	);
}

const MOBILE_STAT = 'h-44 w-[17rem] shrink-0 snap-start';
const MOBILE_PHOTO = 'h-44 w-52 shrink-0 snap-start';

/**
 * Hero stats bento: the four real global stats (`getWorldcooGlobalStats`, prefetched on the route)
 * mixed with photos, formatted for the current locale.
 *
 * Two arrangements of the same tiles. Desktop is the three-column grid with the photos bleeding off
 * the left and right edges. Mobile is TWO horizontally swipeable rows, per the mobile hand-off — not
 * a single stacked column, which is what this used to do: stacked, the four figures pushed the rest of
 * the page down by nearly a screen and a half.
 */
export function Stats() {
	const { t } = useLingui();
	const { language } = useTranslation();
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());

	// `alwaysGroup`: these counters must read as thousands, and Spanish does not group 4 digits by
	// default (1303 → "1303"). The two big figures are shown in full, with no leading "+": the comms
	// team wants the real, live number rather than an abbreviation of it.
	const grouped = { alwaysGroup: true };
	const projectsFunded = formatNumber(language, stats.projectsFunded, 0, grouped);
	const partnerNgos = formatNumber(language, stats.socialOrganizations, 0, grouped);
	const totalRaised = `${formatNumber(language, Math.round(stats.totalRaised.amount), 0, grouped)} €`;
	const microDonations = formatNumber(language, stats.totalDonations, 0, grouped);

	const kids = { src: '/redesign/hero-kids.jpg', alt: t`Children we help` };
	const girl = { src: '/redesign/hero-girl.jpg', alt: t`A child smiling` };

	return (
		<div className="relative pb-16 md:pb-24">
			{/* Anchored to the BENTO, not the viewport: pinned to the edges with a fixed width they only
			    worked at 1440, drifting hundreds of px away on wider screens and overlapping the cards on
			    narrower ones. Each keeps a constant 24px gap from the grid (600px half-width + 24) and grows
			    into the space left, capped so it never becomes a slab. Hidden below 1376px, where there is
			    no room. At 1440 this resolves to the designed 96px flush against the edge. */}
			<PhotoTile
				{...kids}
				className="absolute top-16 right-[calc(50%_+_624px)] hidden h-72 w-[clamp(4rem,calc(50%_-_624px),13.75rem)] min-[1376px]:block"
			/>
			<PhotoTile
				{...girl}
				className="absolute top-16 left-[calc(50%_+_624px)] hidden h-72 w-[clamp(4rem,calc(50%_-_624px),13.75rem)] min-[1376px]:block"
			/>

			{/* Each row LEADS with one of the two headline figures, so both are readable before anyone
			    swipes; what follows is the supporting metric and then the decorative tile. The kids photo
			    is desktop-only — on mobile it would be a second child portrait next to the girl, and the
			    mobile hand-off only shows one.
			    Slightly different speeds so the rows do not travel in lockstep, which reads as one wide
			    block sliding and hides that they are two separate rows you can swipe. */}
			<div className="flex flex-col gap-4 md:hidden">
				<SwipeRow speed={DRIFT_PX_PER_MS}>
					<TotalRaisedTile value={totalRaised} className={MOBILE_STAT} />
					<FundedProjectsTile value={projectsFunded} className={MOBILE_STAT} />
					<PhotoTile {...girl} className={MOBILE_PHOTO} />
				</SwipeRow>
				<SwipeRow speed={DRIFT_PX_PER_MS * 1.35}>
					<MicrodonationsTile value={microDonations} className={MOBILE_STAT} />
					<PartnerNgosTile value={partnerNgos} className={MOBILE_STAT} />
					<CoinsTile className={MOBILE_STAT} />
				</SwipeRow>
			</div>

			<div className="mx-auto hidden max-w-[1200px] gap-4 px-6 md:grid md:grid-cols-3 xl:px-10">
				<div className="grid grid-rows-2 gap-4">
					<FundedProjectsTile value={projectsFunded} />
					<CoinsTile className="min-h-44" />
				</div>
				<TotalRaisedTile value={totalRaised} className="min-h-[22rem]" />
				<div className="grid grid-rows-[1.5fr_1fr] gap-4">
					<MicrodonationsTile value={microDonations} />
					<PartnerNgosTile value={partnerNgos} />
				</div>
			</div>
		</div>
	);
}
